import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';

import { AuthLayout } from '../../components/layout/AuthLayout';
import { Stack } from '../../components/layout/Stack';

export const Route = createFileRoute('/_public/privacy')({
  component: PrivacyPage,
});

function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-text-secondary border-b border-border pb-1.5">
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function LegalItem({
  index,
  title,
  children,
}: {
  index: number;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-5 h-5 rounded-full bg-accent-50 border border-accent-100 flex items-center justify-center text-caption font-bold text-accent-500">
        {index}
      </div>
      <div>
        <h5 className="text-xs font-bold text-text-primary">{title}</h5>
        <p className="text-caption text-text-secondary mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function LegalBullet({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0 mt-1.5" />
      <p className="text-caption text-text-secondary leading-relaxed">{children}</p>
    </div>
  );
}

function PrivacyPage() {
  usePageTitle('Privacy Policy');

  return (
    <AuthLayout
      title="Privacy Policy"
      subtitle="How information is collected, used, and protected in the Kraphene mobile apps."
      cardClassName="max-w-[540px]"
    >
      <Stack gap="6" className="w-full">
        <p className="text-caption text-text-secondary leading-relaxed">
          This policy explains how information is handled in the Kraphene mobile apps. The apps are
          provided to you by the company you belong to (your service provider), which operates your
          account and chit schemes using the Kraphene platform.
        </p>

        <LegalSection title="Information we collect">
          <LegalItem index={1} title="Account and profile details">
            Your name, contact details, membership or account identifiers, and other details your
            service provider — the company you belong to — maintains to identify you in the apps.
          </LegalItem>
          <LegalItem index={2} title="Transaction and chit-auction records">
            Contributions, payments, receipts, auction bids, prize payouts, and settlement records
            generated through your participation in chit schemes operated by the company you belong
            to.
          </LegalItem>
          <LegalItem index={3} title="MPIN-authentication security events">
            Sign-in attempts, MPIN verification outcomes, failed-access attempts, and other
            device-security events recorded to protect your account from unauthorised access.
          </LegalItem>
          <LegalItem index={4} title="Periodic device location">
            The customer app collects your device location periodically to prevent fraud and abuse.
            The agent app collects your device location periodically to verify field operations.
            Location is collected while you are signed in to the apps and is used only for these
            purposes.
          </LegalItem>
        </LegalSection>

        <LegalSection title="How we use information">
          <LegalBullet>
            Provide app features such as viewing chit schemes, paying instalments, and receiving
            digital receipts.
          </LegalBullet>
          <LegalBullet>Authenticate you securely using your MPIN and device binding.</LegalBullet>
          <LegalBullet>
            Record, process, and settle chit-fund operations, including auctions.
          </LegalBullet>
          <LegalBullet>
            Prevent fraud, abuse, and unauthorised access, and verify field operations.
          </LegalBullet>
          <LegalBullet>
            Meet the legal, regulatory, and record-keeping obligations of the company you belong to.
          </LegalBullet>
          <LegalBullet>Provide support and improve the reliability of the apps.</LegalBullet>
        </LegalSection>

        <LegalSection title="Data sharing">
          <LegalBullet>
            The company you belong to, which operates your account and the chit schemes you
            participate in.
          </LegalBullet>
          <LegalBullet>
            Regulators, courts, law-enforcement agencies, and other authorities, where disclosure is
            required by law.
          </LegalBullet>
          <p className="text-caption text-text-secondary leading-relaxed">
            We share your information only with the parties listed above, and we do not sell your
            information.
          </p>
        </LegalSection>

        <LegalSection title="Data security">
          <LegalBullet>
            <b>Device binding:</b> the apps operate only on a device registered to you, so an app
            installed elsewhere cannot access your account.
          </LegalBullet>
          <LegalBullet>
            <b>Encrypted on-device storage:</b> sensitive data held on your phone, including your
            MPIN-protected credentials, is stored encrypted.
          </LegalBullet>
          <LegalBullet>
            <b>Access controls:</b> staff of the company you belong to can access your information
            only as required for their role, under role-based access controls.
          </LegalBullet>
        </LegalSection>

        <LegalSection title="Data retention">
          <p className="text-caption text-text-secondary leading-relaxed">
            Your information is retained for as long as required by the regulatory and
            record-keeping requirements applicable to the company you belong to. Once those periods
            end, the information is deleted or anonymised.
          </p>
        </LegalSection>

        <LegalSection title="Your rights">
          <p className="text-caption text-text-secondary leading-relaxed">
            You may request access to, or correction of, your information by contacting your service
            provider — the company you belong to. Requests are handled in accordance with applicable
            Indian law.
          </p>
        </LegalSection>

        <LegalSection title="Changes to this policy">
          <p className="text-caption text-text-secondary leading-relaxed">
            We may update this policy from time to time. The revised policy will be published in the
            apps along with an updated “Last updated” date. Please review it periodically.
          </p>
        </LegalSection>

        <LegalSection title="Contact">
          <p className="text-caption text-text-secondary leading-relaxed">
            For questions or requests about this policy, use the support contact details shown in
            the apps, or contact your relationship manager at the company you belong to.
          </p>
        </LegalSection>

        <div className="border-t border-border pt-4 flex justify-center items-center text-caption text-text-muted">
          Last updated: September 2026
        </div>
      </Stack>
    </AuthLayout>
  );
}
